import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import SelectField from './SelectField';

jest.mock('components/styleguide/icon/Icon', () => {
  return {
    __esModule: true,
    default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
  };
});

const options = [
  { value: 'one', label: 'Option One' },
  { value: 'two', label: 'Option Two' },
];

describe('SelectField', () => {
  it('renders a select input', () => {
    render(<SelectField id="test" options={options} />);
    expect(screen.getByRole('textbox')).toBeInTheDocument();
  });

  it('renders label associated with select via htmlFor', () => {
    render(<SelectField id="color" label="Color" options={options} />);
    expect(screen.getByLabelText('Color')).toBeInTheDocument();
  });

  it('renders required asterisk hidden from screen readers', () => {
    render(<SelectField id="test" label="Color" required options={options} />);
    const asterisk = screen.getByText('*');
    expect(asterisk).toHaveAttribute('aria-hidden', 'true');
    expect(asterisk.tagName).toBe('SPAN');
  });

  it('renders disabled state', () => {
    const { container } = render(<SelectField id="test" disabled options={options} />);
    const input = container.querySelector('#test') as HTMLInputElement;
    expect(input).toBeDisabled();
  });

  it('renders hint text', () => {
    render(<SelectField id="test" hint="Pick one" options={options} />);
    expect(screen.getByText('Pick one')).toBeInTheDocument();
  });

  it('renders error message with alert role', () => {
    render(<SelectField id="test" error="Selection required" options={options} />);
    expect(screen.getByRole('alert')).toHaveTextContent('Selection required');
  });

  it('does not render alert role for hint', () => {
    render(<SelectField id="test" hint="Just a hint" options={options} />);
    expect(screen.queryByRole('alert')).not.toBeInTheDocument();
  });

  it('renders error icon when error is present', () => {
    const { container } = render(<SelectField id="test" error="Error" options={options} />);
    expect(container.querySelector('[data-icon="triangleAlert"]')).toBeInTheDocument();
  });

  it('renders info icon for hint', () => {
    const { container } = render(<SelectField id="test" hint="Hint" options={options} />);
    expect(container.querySelector('[data-icon="info"]')).toBeInTheDocument();
  });

  describe('accessibility', () => {
    it('sets aria-invalid on container when error is present', () => {
      const { container } = render(<SelectField id="test" error="Required" options={options} />);
      expect(container.querySelector('[aria-invalid="true"]')).toBeInTheDocument();
    });

    it('sets aria-invalid to false when no error', () => {
      const { container } = render(<SelectField id="test" options={options} />);
      expect(container.querySelector('[aria-invalid="false"]')).toBeInTheDocument();
    });

    it('associates message with container via aria-describedby', () => {
      const { container } = render(<SelectField id="color" error="Required" options={options} />);
      expect(container.querySelector('[aria-describedby="color-message"]')).toBeInTheDocument();
      expect(document.getElementById('color-message')).toHaveTextContent('Required');
    });

    it('associates hint with container via aria-describedby', () => {
      const { container } = render(<SelectField id="color" hint="Choose a color" options={options} />);
      expect(container.querySelector('[aria-describedby="color-message"]')).toBeInTheDocument();
      expect(document.getElementById('color-message')).toHaveTextContent('Choose a color');
    });

    it('does not set aria-describedby when no hint or error', () => {
      const { container } = render(<SelectField id="test" options={options} />);
      expect(container.querySelector('[aria-describedby]')).not.toBeInTheDocument();
    });

    it('sets aria-required on container when required', () => {
      const { container } = render(<SelectField id="test" required options={options} />);
      expect(container.querySelector('[aria-required="true"]')).toBeInTheDocument();
    });

    it('does not set aria-required when not required', () => {
      const { container } = render(<SelectField id="test" options={options} />);
      expect(container.querySelector('[aria-required]')).not.toBeInTheDocument();
    });
  });

  it('renders readOnly mode with value label', () => {
    render(<SelectField readOnly value={{ value: 'one', label: 'Option One' }} options={options} />);
    expect(screen.getByText('Option One')).toBeInTheDocument();
    expect(screen.queryByRole('textbox')).not.toBeInTheDocument();
  });

  it('renders hidden input for form submission', () => {
    const { container } = render(
      <SelectField
        id="test"
        name="color"
        value={{ value: 'one', label: 'Option One' }}
        onChange={jest.fn()}
        options={options}
      />
    );
    const hidden = container.querySelector('input[type="hidden"][name="color"]') as HTMLInputElement;
    expect(hidden).toBeInTheDocument();
    expect(hidden.value).toBe('one');
  });

  it('renders data-testid when testId is provided', () => {
    render(<SelectField id="test" testId="my-select" options={options} />);
    expect(screen.getByTestId('my-select')).toBeInTheDocument();
  });

  it('renders placeholder text', () => {
    render(<SelectField id="test" placeholder="Choose..." options={options} />);
    expect(screen.getByText('Choose...')).toBeInTheDocument();
  });

  describe('error clearing', () => {
    it('clears error when selecting an option in uncontrolled mode', () => {
      render(<SelectField id="test" error="Required" options={options} />);
      expect(screen.getByRole('alert')).toBeInTheDocument();

      const input = screen.getByRole('textbox');
      fireEvent.change(input, { target: { value: 'Option One' } });
      fireEvent.keyDown(input, { key: 'Enter', code: 'Enter' });

      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
    });

    it('clears error when selecting an option in controlled mode', () => {
      const handleChange = jest.fn();
      render(
        <SelectField id="test" error="Required" options={options} value={null} onChange={handleChange} />
      );
      expect(screen.getByRole('alert')).toBeInTheDocument();

      const input = screen.getByRole('textbox');
      fireEvent.change(input, { target: { value: 'Option One' } });
      fireEvent.keyDown(input, { key: 'Enter', code: 'Enter' });

      expect(screen.queryByRole('alert')).not.toBeInTheDocument();
      expect(handleChange).toHaveBeenCalled();
    });

    it('syncs displayError when error prop changes', () => {
      const { rerender } = render(<SelectField id="test" error="First error" options={options} />);
      expect(screen.getByText('First error')).toBeInTheDocument();

      rerender(<SelectField id="test" error="Second error" options={options} />);
      expect(screen.getByText('Second error')).toBeInTheDocument();
    });

    it('clears displayError when error prop is removed', () => {
      const { rerender } = render(<SelectField id="test" error="Error" options={options} />);
      expect(screen.getByText('Error')).toBeInTheDocument();

      rerender(<SelectField id="test" options={options} />);
      expect(screen.queryByText('Error')).not.toBeInTheDocument();
    });
  });
});
